// The user's blunder and miss rules, kept as `settings/mistakes.json`: read
// once, served to every reader through the `mistakeRules` API, and announced
// to them whenever the settings section saves new ones. Missing or broken
// files give the defaults.
import { DEFAULT_MISTAKE_RULES, normalizeMistakeRules, sameMistakeRules, type MistakeRules, type MistakeRulesSource } from '@chess/api-model';

export const FILE = 'mistakes.json';

/** The stored shape. */
export interface MistakeRulesFile {
  schemaVersion: 1;
  rules: MistakeRules;
}

/** What the service needs of the storage, so it runs on fakes in tests. */
export interface RulesStorage {
  readText(path: string): Promise<string | undefined>;
  write(path: string, data: string): Promise<void>;
}

/** The rules a stored file holds; the defaults when it is missing, of another version or unreadable. */
export function parseRulesFile(text: string | undefined): MistakeRules {
  if (!text) return { ...DEFAULT_MISTAKE_RULES };
  try {
    const raw = JSON.parse(text) as Partial<MistakeRulesFile>;
    return raw.schemaVersion === 1 ? normalizeMistakeRules(raw.rules) : { ...DEFAULT_MISTAKE_RULES };
  } catch {
    return { ...DEFAULT_MISTAKE_RULES };
  }
}

export class MistakeRulesService implements MistakeRulesSource {
  private current: MistakeRules = { ...DEFAULT_MISTAKE_RULES };
  private readonly listeners = new Set<(rules: MistakeRules) => void>();
  private readonly read: Promise<MistakeRules>;
  private readonly storage: Promise<RulesStorage>;
  private readonly warn: (message: string, fields?: Record<string, unknown>) => void;

  constructor(storage: Promise<RulesStorage>, warn: (message: string, fields?: Record<string, unknown>) => void = () => undefined) {
    this.storage = storage;
    this.warn = warn;
    this.read = this.storage
      .then((s) => s.readText(FILE))
      .then(parseRulesFile)
      .catch((error) => {
        this.warn('the mistake rules could not be read; using the defaults', { error: String(error) });
        return { ...DEFAULT_MISTAKE_RULES };
      })
      .then((rules) => {
        this.change(rules);
        return rules;
      });
  }

  get(): MistakeRules {
    return this.current;
  }

  ready(): Promise<MistakeRules> {
    return this.read;
  }

  subscribe(listener: (rules: MistakeRules) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async save(rules: Partial<MistakeRules>): Promise<MistakeRules> {
    await this.read;
    const next = normalizeMistakeRules({ ...this.current, ...rules });
    const file: MistakeRulesFile = { schemaVersion: 1, rules: next };
    await (await this.storage).write(FILE, JSON.stringify(file));
    this.change(next);
    return next;
  }

  /** Takes new rules and tells the listeners, unless nothing changed. */
  private change(rules: MistakeRules): void {
    if (sameMistakeRules(rules, this.current)) return;
    this.current = rules;
    for (const listener of [...this.listeners]) {
      try {
        listener(rules);
      } catch (error) {
        this.warn('a mistake rules listener failed', { error: String(error) });
      }
    }
  }
}
